import { useState, useCallback, useMemo } from "react";
import {
  SortDirection,
  GfFlyerItemCategory,
} from "@swagger/typescript-fetch-goflyer";
import { removeDoubleQuotes } from "../../../../utils/utils";
import { AutocompleteOption } from "../../../components/CustomAutocomplete";
import { useTranslation } from "react-i18next";
import { messages } from "../../../../locales/messages";

export interface ImageFiltersState {
  category: string[];
  date?: SortDirection;
  size?: SortDirection;
  format: string[];
}

export interface FilterSelectOption {
  name: string;
  selectOptions: AutocompleteOption<string>[];
  multiple?: boolean;
  onSelectMultiple?: (value: string[]) => void;
  onSelectSingleItem?: (value: string) => void;
}

export interface UseImageFiltersReturn {
  filtersState: ImageFiltersState;
  handleCategoryChange: (value: string[]) => void;
  handleDateChange: (value: string) => void;
  handleSizeChange: (value: string) => void;
  handleFormatChange: (value: string[]) => void;
  resetFilters: () => void;
  selectList: FilterSelectOption[];
}

const initialFiltersState: ImageFiltersState = {
  category: [],
  date: undefined,
  size: undefined,
  format: [],
};

export const useImageFilters = (
  flyerItemCategories?: GfFlyerItemCategory[],
  onFilterChange?: () => void,
): UseImageFiltersReturn => {
  const { t } = useTranslation();

  const categoryOptions = useMemo(
    () =>
      flyerItemCategories?.map((category) => {
        const categoryName = category.name || "";
        const categoryNameLower = categoryName.toLowerCase()?.trim();

        let mappedValue = categoryName;
        if (categoryNameLower === "frozen & dairy") {
          mappedValue = "Frozen and diary";
        } else if (categoryNameLower === "seafood & meat") {
          mappedValue = "Fish and meat";
        } else if (categoryNameLower === "beauty & health") {
          mappedValue = "Health and beauty";
        }

        return {
          value: mappedValue,
          label: categoryName,
        };
      }) || [],
    [flyerItemCategories],
  );

  const [filtersState, setFiltersState] =
    useState<ImageFiltersState>(initialFiltersState);

  const handleCategoryChange = useCallback(
    (value: string[]) => {
      setFiltersState((prev) => ({ ...prev, category: value }));
      onFilterChange?.();
    },
    [onFilterChange],
  );

  const handleDateChange = useCallback(
    (value: string) => {
      const cleaned = removeDoubleQuotes(value);
      const sortDirectionValue = cleaned
        ? SortDirection[cleaned as keyof typeof SortDirection]
        : undefined;
      setFiltersState((prev) => ({ ...prev, date: sortDirectionValue }));
      onFilterChange?.();
    },
    [onFilterChange],
  );

  const handleSizeChange = useCallback(
    (value: string) => {
      const cleaned = removeDoubleQuotes(value);
      const sortDirectionValue = cleaned
        ? SortDirection[cleaned as keyof typeof SortDirection]
        : undefined;
      setFiltersState((prev) => ({ ...prev, size: sortDirectionValue }));
      onFilterChange?.();
    },
    [onFilterChange],
  );

  const handleFormatChange = useCallback(
    (value: string[]) => {
      setFiltersState((prev) => ({ ...prev, format: value }));
      onFilterChange?.();
    },
    [onFilterChange],
  );

  const resetFilters = useCallback(() => {
    setFiltersState(initialFiltersState);
  }, []);

  const selectList = useMemo<FilterSelectOption[]>(
    () => [
      {
        name: t(messages.Category()),
        selectOptions: categoryOptions,
        multiple: true,
        onSelectMultiple: handleCategoryChange,
      },
      {
        name: t(messages.Date()),
        selectOptions: [
          { value: "", label: "None" },
          { value: "Desc", label: "Newest First" },
          { value: "Asc", label: "Oldest First" },
        ],
        onSelectSingleItem: handleDateChange,
      },
      {
        name: t(messages.Size()),
        selectOptions: [
          { value: "", label: "None" },
          { value: "Asc", label: "Smallest First" },
          { value: "Desc", label: "Largest First" },
        ],
        onSelectSingleItem: handleSizeChange,
      },
    ],
    [
      t,
      messages,
      categoryOptions,
      handleCategoryChange,
      handleDateChange,
      handleSizeChange,
    ],
  );

  return {
    filtersState,
    handleCategoryChange,
    handleDateChange,
    handleSizeChange,
    handleFormatChange,
    resetFilters,
    selectList,
  };
};
